import React, { useCallback, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import PageBanner from 'LuxKit/Banners/PageBanner'
import { getDismissed, setDismissed as sessionSetDismissed } from 'luxPlus/storage/renewalBanner'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import getIsLoyaltyLuxPlusRenewalBonusEnabled from 'loyalty/selectors/getIsLoyaltyLuxPlusRenewalBonusEnabled'
import FormatLoyaltyStatusCredits from 'loyalty/components/Formatters/FormatLoyaltyStatusCredits'
import FormatLoyaltyBonusPoints from 'loyalty/components/Formatters/FormatLoyaltyBonusPoints'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { LOYALTY_LUX_PLUS_RENEWAL_BONUS_STATUS_CREDITS, LOYALTY_LUX_PLUS_RENEWAL_BONUS_POINTS } from 'loyalty/constants/loyaltyBonusPoints'
import * as Analytics from 'analytics/analytics'
import purchaseLuxPlus from 'actions/luxPlus/purchaseLuxPlus'
import { fetchProductSubscription } from 'actions/LuxPlusActions'

function LuxPlusRenewalBanner() {
  const intl = useIntl()
  const [dismissed, setDismissed] = useState(getDismissed())
  const isLoyaltyLuxPlusRenewalBonusEnabled = useAppSelector(getIsLoyaltyLuxPlusRenewalBonusEnabled)
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchProductSubscription())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRenewal = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'manual_renewal_purchase',
      action: 'add_to_cart_lux_plus_standalone',
      category: LUXURY_PLUS.ANALYTICS_CATEGORY,
      type: 'interaction',
    })
    dispatch(purchaseLuxPlus({ hasJoinFee: false }))
  }, [dispatch])

  const renewalStatus = useAppSelector(state => state.auth.account.luxPlus.member.subscription.item?.renewalStatus)
  const hasAutoRenewal = renewalStatus === 'ALLOW_RENEWAL'

  const dismiss = useCallback(() => {
    setDismissed(true)
    sessionSetDismissed()
  }, [])

  if (dismissed) return null

  const renewNowLabel = intl.formatMessage({
    id: 'JyT48gJbdG',
    defaultMessage: 'Renew now',
    description: 'Primary action button text to renew LuxPlus+ membership',
  })
  const manageMembershipLabel = intl.formatMessage({
    id: 'CzduFvXxOE',
    defaultMessage: 'Manage membership',
    description: 'Secondary action button text to manage LuxPlus+ membership',
  })

  return <PageBanner
    kind="critical"
    onDismiss={dismiss}
    primaryActionButtonProps={{
      onClick: handleRenewal,
      children: renewNowLabel,
    }}
    secondaryActionButtonProps={{
      to: `/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}`,
      children: manageMembershipLabel,
    }}
  >
    {hasAutoRenewal && <b><FormattedMessage
      id="m/qewL9kXb"
      defaultMessage="Your {programName} Membership failed to renew due to a payment issue. "
      description="Critical banner message when LuxPlus+ membership renewal fails due to payment issue"
      values={{ programName: LUXURY_PLUS.PROGRAM_NAME }}
    />
    </b>}
    {!hasAutoRenewal && <b><FormattedMessage
      id="PnOPIhWNzz"
      defaultMessage="Your {programName} Membership will expire soon. "
      description="Critical banner message when LuxPlus+ membership is about to expire"
      values={{ programName: LUXURY_PLUS.PROGRAM_NAME }}
    />
    </b>}
    {isLoyaltyLuxPlusRenewalBonusEnabled && <FormattedMessage
      id="8QQsuhoKWl"
      defaultMessage="Renew now to receive <bold>{statusCredits} and {bonusPoints}</bold> with {loyaltyProgram}."
      description="Banner message encouraging LuxPlus+ renewal with loyalty rewards bonus"
      values={{
        statusCredits: <FormatLoyaltyStatusCredits statusCredits={LOYALTY_LUX_PLUS_RENEWAL_BONUS_STATUS_CREDITS} />,
        bonusPoints: <FormatLoyaltyBonusPoints points={LOYALTY_LUX_PLUS_RENEWAL_BONUS_POINTS} />,
        loyaltyProgram: getLoyaltyProgramDisplayName(),
        bold: (chunks) => <b>{chunks}</b>,
      }}
    />}
    {!isLoyaltyLuxPlusRenewalBonusEnabled && <>{intl.formatMessage({
      id: 'qHNMnEB9H5',
      defaultMessage: 'Your benefits are on hold until payment is completed.',
      description: 'Banner message when LuxPlus+ benefits are on hold due to payment failure',
    })}
    </>}
  </PageBanner>
}

export default LuxPlusRenewalBanner
